import * as BoardConstants from "@/game/constants/boardConstants";
import type { GameBoardOverlayInterface } from "@/game/context/GameBoardOverlayContext";
import { configAreaTargetHighlight, configCheckerHighlight } from "@/game/utils/boardHighligts";
import { getCheckerPositions, renderCheckerStack } from "@/game/utils/checkerDisplayUtils";
import type { GameState, PointState } from "@nextgammon/shared";
import { Constants } from "@nextgammon/shared";
import { useEffect, useMemo } from "react";

import { Container, Text } from "@pixi/react";
import { Assets, Rectangle, TextStyle } from "pixi.js";

interface GameBoardPointProps {
  gameState: GameState;
  pointIndex: number;
  point: PointState;
  isTop: boolean;
  highlightForSingle: boolean;
  highlightForCombo: boolean;
  isSelected: boolean;
  pointArea: [number, number, number, number];
  boardScale: number;
  overlayInterface: GameBoardOverlayInterface | null;
  onClick: (index: number) => void;
}

export function GameBoardPoint({
  gameState,
  pointIndex,
  isTop,
  point,
  highlightForSingle,
  highlightForCombo,
  isSelected,
  pointArea,
  boardScale,
  overlayInterface,
  onClick
}: GameBoardPointProps) {
  // Load the player checker texture
  const playerTexture = useMemo(() => Assets.get("board-checker-local"), []);

  // Load the opponent checker texture
  const opponentTexture = useMemo(() => Assets.get("board-checker-opponent"), []);

  // Load selection highlight texture for the point
  const selectedPointTexture = useMemo(() => Assets.get("highlight-point"), []);

  // Load selection highlight texture for the top checker
  const selectedCheckerTexture = useMemo(() => Assets.get("highlight-checker"), []);

  const texture = point
    ? point.player === Constants.PLAYER_WHITE
      ? playerTexture
      : opponentTexture
    : null;
  const labelOffset = texture ? (texture.height * boardScale - 10) / 2 : 0;
  const highlightOffset = boardScale * BoardConstants.POINT_HIGHLIGHT_OFFSET;
  const clickRegion = pointArea
    ? new Rectangle(pointArea[0], pointArea[1], pointArea[2], pointArea[3])
    : null;

  const checkerPosition = useMemo(() => {
    if (!pointArea || !point || point.count === 0 || !texture || !texture.valid) return [];

    return getCheckerPositions(
      pointArea,
      point.count,
      texture.height * boardScale * BoardConstants.POINT_CHECKER_SCALE,
      isTop
    );
  }, [pointArea, point, boardScale, isTop, texture]);
  const topCheckerPosition = checkerPosition.length
    ? checkerPosition[checkerPosition.length - 1]
    : null;

  // Show or hide the selected state sprite
  useEffect(() => {
    if (!overlayInterface || !selectedCheckerTexture || !pointArea || !topCheckerPosition) return;

    const key = `selected-point-${pointIndex}`;
    if (isSelected && point) {
      const highlight = configCheckerHighlight(
        selectedCheckerTexture,
        topCheckerPosition,
        boardScale
      );

      overlayInterface.addSpriteWithTween(key, highlight.sprite, highlight.group, true);
    } else {
      overlayInterface.removeSprite(key);
    }
    // Cleanup on unmount
    return () => {
      overlayInterface.removeSprite(key);
    };
  }, [isSelected, pointArea, boardScale, selectedCheckerTexture, overlayInterface]);

  // Show or hide the potential target state sprite
  useEffect(() => {
    if (!overlayInterface || !selectedPointTexture || !pointArea) return;

    const key = `target-point-${pointIndex}`;
    // TODO - differentiate between single and combo highlights
    if (point && (highlightForSingle || highlightForCombo)) {
      const position = {
        x: pointArea[0] + pointArea[2] / 2,
        y: pointArea[1] + (isTop ? pointArea[3] + highlightOffset : -highlightOffset)
      };
      const highlight = configAreaTargetHighlight(
        selectedPointTexture,
        position,
        isTop ? Math.PI : 0,
        boardScale
      );

      overlayInterface.addSpriteWithTween(key, highlight.sprite, highlight.tween, true);
    } else {
      overlayInterface.removeSprite(key);
    }
    // Cleanup on unmount
    return () => {
      overlayInterface.removeSprite(key);
    };
  }, [
    highlightForSingle,
    highlightForCombo,
    pointArea,
    boardScale,
    selectedPointTexture,
    overlayInterface,
    highlightOffset
  ]);

  return (
    <Container hitArea={clickRegion} eventMode="static" pointerdown={onClick}>
      {texture &&
        pointArea &&
        point &&
        renderCheckerStack(
          texture,
          pointArea,
          point.count,
          boardScale * BoardConstants.POINT_CHECKER_SCALE,
          boardScale,
          isTop,
          isSelected
        )}
      {point && point.count > 6 && (
        <Text
          text={point.count.toString()}
          x={pointArea[0] + pointArea[2] / 2}
          y={pointArea[1] + (isTop ? pointArea[3] - labelOffset : labelOffset)}
          anchor={[0.5, 0.5]}
          style={
            new TextStyle({
              fontFamily: "Arial",
              fontSize: 64 * boardScale,
              fill: 0xffffff,
              stroke: 0x000000,
              strokeThickness: 3
            })
          }
        />
      )}
      {BoardConstants.BOARD_DEBUG_RENDERING && (
        <Text
          text={(pointIndex + 1).toString()}
          x={pointArea[0] + pointArea[2] / 2}
          y={pointArea[1] + (isTop ? 0 : pointArea[3])}
          anchor={[0.5, isTop ? 1 : 0]}
          style={
            new TextStyle({
              fontFamily: "Arial",
              fontSize: 64 * boardScale,
              fill: 0xffffff,
              stroke: 0x000000,
              strokeThickness: 2
            })
          }
        />
      )}
    </Container>
  );
}
